import { useEffect, useMemo, useRef, useState } from 'react';
import AppointmentUtils from 'services/AppointmentUtils';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';

const RETRY_TIMEOUT = 60 * 60 * 1000;
const DEFAULT_ARRAY: string[] = [];
const DEFAULT_ITEMS: { patient_id?: string }[] = [];

function useFetchPatients(items: { patient_id?: string }[] = DEFAULT_ITEMS, ids?: string[]) {
  const dispatch = useAppDispatch();
  const patients = useAppSelector((state) => state.patients.byId);
  const [loadingPatients, setLoadingPatients] = useState(false);
  // Ids the server did not return (deleted patient, other clinic, error). They
  // are held back until RETRY_TIMEOUT so that one unresolvable id cannot block
  // every other patient from loading.
  const [backedOffIds, setBackedOffIds] = useState<string[]>(DEFAULT_ARRAY);
  const mountedRef = useRef(true);
  const retryTimersRef = useRef<ReturnType<typeof setTimeout>[]>([]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      retryTimersRef.current.forEach((timer) => clearTimeout(timer));
      retryTimersRef.current = [];
    };
  }, []);

  const patientsToGetIds = useMemo(() => {
    const missing = ids
      ? ids.filter((id) => !!id && !patients[id])
      : AppointmentUtils.getMissingPatients(items, patients);
    const pending = backedOffIds.length
      ? missing.filter((id) => !backedOffIds.includes(id))
      : missing;
    // Keep a stable identity when there is nothing to fetch: call sites rebuild
    // `items` on every render, and the effect below must not re-run for that.
    return pending.length === 0 ? DEFAULT_ARRAY : pending;
  }, [items, patients, ids, backedOffIds]);

  useEffect(() => {
    if (patientsToGetIds.length === 0 || loadingPatients) return;

    const requestedIds = patientsToGetIds;
    const backOff = () => {
      if (!mountedRef.current) return;
      setLoadingPatients(false);
      setBackedOffIds((current) =>
        current.concat(requestedIds.filter((id) => !current.includes(id))),
      );
      const timer = setTimeout(() => {
        retryTimersRef.current = retryTimersRef.current.filter((pending) => pending !== timer);
        if (!mountedRef.current) return;
        setBackedOffIds((current) => current.filter((id) => !requestedIds.includes(id)));
      }, RETRY_TIMEOUT);
      retryTimersRef.current.push(timer);
    };

    setLoadingPatients(true);
    dispatch(api.Patients.getMultiplePatients({ multipleValuesPayload: { values: requestedIds } }))
      .then((fetchedPatients) => {
        if (!mountedRef.current) return;
        if (fetchedPatients?.length) {
          setLoadingPatients(false);
        } else {
          backOff();
        }
      })
      .catch(backOff);
  }, [patientsToGetIds, dispatch, loadingPatients]);
  return [loadingPatients];
}

export default useFetchPatients;
